import 'amazon-connect-streams';
import type { AxiosResponse } from 'axios';
import FHIRFetcher from 'data/FHIRFetcher';
import type { Patient } from 'fhir/r4';
import { payerOrgId } from 'fhir-sdoh/test/test-config';
import { useBroadcastChannel } from 'hooks/use-broadcast-channel';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { DropdownProps } from 'semantic-ui-react';
import type { FlatPatient } from 'types/patient-data';

import * as connectPoster from '../data/ConnectPoster';
import * as FHIRUtilsCB from '../utils/FHIRUtils';

import { useAppState } from './AppContext';



interface AmazonConnectContextType {
  ccpInitialized: boolean;
  loggedInCCP: boolean;
  mobilePhoneNumber?: string | undefined;
  callAnswered?: boolean | undefined;
  homePhoneNumber?: string | undefined;
  loading?: boolean | undefined;
  /** See the SUSPECTED DEFECT note at the `patient` state below for the union. */
  patient?: Patient | FlatPatient | null | undefined;
  setPatient?: React.Dispatch<React.SetStateAction<Patient | FlatPatient | null | undefined>> | undefined;
  chooseMemberModal: boolean;
  /**
   * The caller row's member, chosen from a message log.
   *
   * `Patient | null` because the seven `*Row` components that write it all pass
   * either a `Patient` or `null` (`handleSelectCallerCard(patient: Patient)` /
   * `setSelectedCallerCard(null)`), and consumers read only `.id`,
   * `.managingOrganization` and hand the whole value to `parseFHIRPatientName`.
   */
  selectedCallerCard?: Patient | null | undefined;
  setSelectedCallerCard?: React.Dispatch<React.SetStateAction<Patient | null | undefined>> | undefined;
  commPayloadState?: Payload | undefined;
  selectedMessage?: string | undefined;
  callerOrgId?: string | undefined;
  outboundOrgId?: string | undefined;
  setOutboundOrgId?: React.Dispatch<React.SetStateAction<string | undefined>> | undefined;
  inboundCallerResources?: (Patient | null)[] | null | undefined;
  incomingCall?: boolean | undefined;
  subjectErrorMessage?: boolean | undefined;
  contactId?: string | undefined;
  initialContactId?: string | undefined;
  enableStopRecording: boolean;
  stopRecordingLoader: boolean;
  stopRecordingResponse?: string | undefined;
  error?: string | undefined;
  resetCallCenter: () => void;
  buildPayload: (
    contact: connect.Contact,
    message: string,
    memberName: string,
    practitionerName: string,
    patientId: string,
    state: string,
    contactId: string,
    orgId: string,
    phoneNumber: string,
    orgPhoneNumber: string
  ) => Payload;
  getPatients: (phoneNumber: string, orgId: string) => Promise<(Patient | null)[]>;
  formatInboundPhoneNumber: (phoneNumber: string | undefined) => string;
  /**
   * Wired straight to a semantic-ui `Dropdown`'s `onChange`, so the second
   * argument is a `DropdownProps` whose `value` is
   * `string | number | boolean | (string | number | boolean)[] | undefined` —
   * narrower than the `{ value: string }` this handler's body assumes. The
   * options it is given are all string-valued.
   */
  handleMessageSelection: (event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => void;
  createCommunication: (payload: Payload) => Promise<AxiosResponse | undefined>;
  handleInboundCall: (commData: Payload) => void;
  handleStopRecording: () => Promise<void>;
  showNewPatient: (selectedPatient: Patient, taskId?: string, patientId?: string) => void;
  isMessagesTab: boolean;
  setIsMessagesTab: React.Dispatch<React.SetStateAction<boolean>>;
  forceReinitializeCCP: () => void;
}

export type Payload = {
  type: string;
  sender: string;
  message: string;
  patientId: string | undefined;
  received?: string | undefined;
  sent?: string | undefined;
  contactFlow: string;
  contactState: string;
  contactId: string;
  orgId: string;
  phoneNumber: string;
  dialedNumber: string;
};

/*
 * SUSPECTED DEFECT (softphone silently points at a literal "undefined" host when the
 * Connect build env is unset) — REPORTED, NOT FIXED.
 *
 * `ImportMetaEnv` declares every `VITE_*` optional, deliberately: its doc states only
 * the `requiredBuildEnv` subset in `service.manifest.json` is guaranteed, enforced at
 * build time rather than by the type. None of the four Connect variables is in that
 * subset — it lists only the Cognito and core-API names.
 *
 * These four were annotated `: string`, which asserted a guarantee the build does not
 * make. On a deployment built without them, `CCPURL` becomes the string
 * `"https://undefined.my.connect.aws/connect/"` and the stop-recording call posts to
 * `/hooks/stop-call-recording/undefined` — both well-formed requests to the wrong
 * place, so the softphone fails as a remote error rather than a missing-config error.
 *
 * The annotations now state what the declaration says. Adding them to
 * `requiredBuildEnv` is the real fix and is a deployment decision, not a typing one.
 */
const INSTANCE_ID: string | undefined = import.meta.env.VITE_CONNECT_INSTANCE_ID;
const INSTANCE_ID_CODE: string | undefined = import.meta.env.VITE_CONNECT_INSTANCE_ID_CODE;
const CCPURL: string = `https://${INSTANCE_ID}.my.connect.aws/connect/`;
const _LOGIN_URL: string | undefined = import.meta.env.VITE_CONNECT_LOG_IN_SITE;
const REGION: string = 'us-east-1';
const _CALL_OPTIONS = [
  'Customer Service',
  'Consult',
  'Outreach',
  'Resource',
  'Triage',
  'Food',
  'Return Voice Mail',
  'Surveys',
];
const RINGTONE_URL: string | undefined = import.meta.env.VITE_CONNECT_RINGTONE_URL;

const AmazonConnectContext = createContext<AmazonConnectContextType | undefined>(undefined);

export const AmazonConnectProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authState, appState } = useAppState();

  const { isLeader, sendMessage, messages } = useBroadcastChannel<{ type: string }>(
    'amazon-connect-channel',
  );

  const [ccpInitialized, setCCPInitialized] = useState(false);
  const [loggedInCCP, setLoggedInCCP] = useState(false);

  const [mobilePhoneNumber, setMobilePhoneNumber] = useState<string | undefined>();
  const [callAnswered, setCallAnswered] = useState<boolean | undefined>(false);
  const [homePhoneNumber, setHomePhoneNumber] = useState<string | undefined>();
  const [loading, setLoading] = useState<boolean | undefined>(false);

  /*
   * SUSPECTED DEFECT — this state is WRITTEN with raw FHIR `Patient` resources
   * and READ as if it were a flattened one.
   *
   * Every write in this file stores `patients.entry[0]?.resource` or
   * `callers[0]`, i.e. what `/Patient/?organization=…&telecom=…` returned. But
   * the contact-event effect below reads `MEMBER?.mobilePhone?.[0]`,
   * `MEMBER?.homePhone?.[0]` and `MEMBER?.fullName` — none of which exist on a
   * FHIR `Patient` (verified against the installed `@types/fhir` 0.0.34; they
   * are `FlatPatient` fields produced by `FHIRUtils.parseFHIRPatient`).
   *
   * So `setMobilePhoneNumber`/`setHomePhoneNumber` always store `undefined`, and
   * `memberName` always falls through to the `parseFHIRPatientName(DEFAULT_CALLER)`
   * arm rather than using `MEMBER.fullName`.
   *
   * Both sides' assumptions: the writers guarantee a FHIR resource; the readers
   * assume the flattened projection. The only external writer
   * (`components/patient/communication/index.tsx`) is commented out, so nothing
   * currently supplies the flattened shape the readers want.
   *
   * Typed as the union of both shapes because narrowing to either would make one
   * side's reads a compile error and hide the mismatch. Reported, not fixed —
   * flattening at the write sites would change which phone numbers the softphone
   * displays.
   */
  const [patient, setPatient] = useState<Patient | FlatPatient | null | undefined>(undefined);

  const [isMessagesTab, setIsMessagesTab] = useState<boolean>(false);

  // NOTE: for main page
  const [chooseMemberModal, setChooseMemberModal] = useState<boolean>(false);
  const [selectedCallerCard, setSelectedCallerCard] = useState<Patient | null | undefined>(undefined);
  const [commPayloadState, setCommPayloadState] = useState<Payload | undefined>(undefined);

  // NOTE: patinet profile
  const [selectedMessage, setSelectedMessage] = useState<string | undefined>('Outbound Call');

  const [callerOrgId, setCallerOrgId] = useState<string | undefined>();
  const [outboundOrgId, setOutboundOrgId] = useState<string | undefined>();
  // `null` as well as `undefined`: `resetCallCenter` writes `[]` and then `null`
  // to this same state, and the contact handler writes `callers ?? null`.
  const [inboundCallerResources, setInboundCallerResources] = useState<(Patient | null)[] | null | undefined>();
  const [incomingCall, setIncomingCall] = useState<boolean | undefined>(false);
  const [subjectErrorMessage, setSubjectErrorMessage] = useState<boolean | undefined>(false);

  const [contactId, setContactId] = useState<string | undefined>(undefined);
  const [initialContactId, setInitialContactId] = useState<string | undefined>(undefined);
  const [enableStopRecording, setEnableStopRecording] = useState<boolean>(false);
  const [stopRecordingLoader, setStopRecordingLoader] = useState<boolean>(false);
  const [stopRecordingResponse, setStopRecordingResponse] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);

  const callSubject: string = 'Outbound Call';

  // const proxyURL = import.meta.env.VITE_API_ENDPOINT + '/fhir/';
  // const fhirProxyClient = new FHIRProxyClient(authState.token, proxyURL);

  const [ccpElementRef, setCcpElementRef] = useState<HTMLElement | null>(null);
  const [initializationInProgress, setInitializationInProgress] = useState(false);

  const showNewPatient = (selectedPatient: Patient, taskId?: string, patientId?: string) => {
    if (taskId) {
      window.open(`/patients/${selectedPatient.id}?taskId=${taskId}`, selectedPatient.id);
    } else if (patientId) {
      window.open(`/patients/${patientId}`, patientId);
    } else {
      window.open(`/patients/${selectedPatient.id}`, selectedPatient.id);
    }
  };

  const resetCallCenter = () => {
    setInboundCallerResources([]);
    setIncomingCall(false);
    setCallerOrgId(undefined);
    setInboundCallerResources(null);
    setLoading(false);
    setEnableStopRecording(false);
    setStopRecordingLoader(false);
    setStopRecordingResponse(undefined);
    setError(undefined);
    setContactId(undefined);
    setInitialContactId(undefined);
  };

  const buildPayload = useCallback(
    (
      contact: connect.Contact,
      message: string,
      memberName: string,
      practitionerName: string,
      patientId: string,
      state: string,
      contactId: string,
      orgId: string = 'pw',
      phoneNumber: string,
      orgPhoneNumber: string
    ): Payload => {
      const contactType = contact.getType()?.toString() || '';
      const callIsInbound = contact.isInbound();
      const date = new Date().toISOString();

      const isMissedCall = callIsInbound && state === 'error';
      const isInboundCall = callIsInbound && (state === 'ended' || callAnswered);

      const payload: Payload = {
        type: contactType,
        sender: callIsInbound ? memberName : practitionerName,
        message: isMissedCall ? 'MISSED CALL' : isInboundCall ? 'Inbound Call' : message,
        patientId,
        received: callIsInbound ? date : undefined,
        sent: callIsInbound ? undefined : date,
        contactFlow: '',
        contactState: state,
        contactId,
        orgId,
        phoneNumber: callIsInbound ? phoneNumber : orgPhoneNumber,
        dialedNumber: callIsInbound ? orgPhoneNumber : phoneNumber,
      };

      return payload;
    },
    [callAnswered]
  );

  const getPatients = useCallback(
    async (phoneNumber: string, orgId: string) => {
      const key = `/Patient/?organization=${orgId}&telecom=${phoneNumber}`;
      // `authState.token` is `string | null`; every path into this provider runs
      // behind `authState.loggedIn`. The `!`s here and at the two
      // `ConnectHooksPoster` calls below record that rather than substituting a
      // token.
      const patients = await FHIRFetcher(key, authState.token!);

      // TODO: if a number calls and is not associated with a patient this will break.
      if (!patients?.entry || patients.entry.length === 0) {
        console.warn('No patients found for the given phone number and organization.');
        return [];
      }

      // `as Patient`: a `/Patient/?…` search is single-type, so the entries are
      // Patients even though `BundleEntry.resource` is the whole union.
      setPatient((patients.entry[0]?.resource ?? null) as Patient | null);

      // NOTE: should return a list, but we only need first patient that is calling.
      return patients.entry.map((entry) => (entry.resource ?? null) as Patient | null);
    },
    [authState.token, setPatient]
  );

  /**
   * SUSPECTED DEFECT (inbound call with no `phoneNumber` attribute throws) — REPORTED,
   * NOT FIXED. Both call sites pass `attributes?.['phoneNumber']?.value`, and
   * `connect.AttributeDictionary` declares its entries `{ name, value } | undefined`,
   * so AWS itself states the attribute may be absent. The caller therefore hands over
   * `string | undefined`; this body dereferences `.replace` unconditionally.
   *
   * A Connect contact routed by a flow that does not set `phoneNumber` throws
   * `TypeError: Cannot read properties of undefined (reading 'replace')` inside the
   * `onConnecting`/`onEnded` handler. In `onConnecting` that aborts before
   * `setIncomingCall(true)`, so the softphone never shows the incoming call; in
   * `onEnded` it aborts before `buildPayload`, so the call is never logged and the
   * communication record for that call is silently lost.
   *
   * The parameter states the union the callers actually pass rather than the `string`
   * they do not, which is what makes the throw visible here instead of at the call
   * site. Adding a guard would convert the throw into a silent no-op — a different
   * product behaviour, and one that would drop the call log without any signal.
   */
  const formatInboundPhoneNumber = (phoneNumber: string | undefined): string => {
    return phoneNumber!.replace(/\+1/g, '');
  };

  const handleMessageSelection = (e: React.SyntheticEvent<HTMLElement>, { value }: DropdownProps) => {
    const callSubject = value as string;
    setSelectedMessage(callSubject);
    setSubjectErrorMessage(false);
  };

  const createCommunication = useCallback(
    async (payload: Payload) => {
      try {
        const response = await connectPoster.ConnectHooksPoster(
          authState.token!,
          '/hooks/create-communication',
          payload
        );
        return response;
      } catch (err) {
        console.error('Error creating communication:', err);
        throw err; // Re-throw error for further handling
      }
    },
    [authState.token]
  );

  const handleInboundCall = (commData: Payload) => {
    const inboundPayload = {
      ...commData,
      // Unguarded today: `handleInboundCall` is only invoked from the
      // choose-member modal, which is shown after a card has been selected.
      patientId: selectedCallerCard!.id,
      sender: FHIRUtilsCB.parseFHIRPatientName(selectedCallerCard),
      orgId: payerOrgId,
    };

    createCommunication(inboundPayload)
      .then(() => {
        resetCallCenter();
        setCallAnswered(false);
        setChooseMemberModal(false);
      })
      .catch((err) => {
        console.error('Error handling inbound call:', err);
      });
  };

  const handleStopRecording = async () => {
    setStopRecordingLoader(true);
    try {
      const stopRecPayload = {
        contactId: contactId,
        initialContactId: initialContactId ?? contactId,
      };

      const response = await connectPoster.ConnectHooksPoster(
        authState.token!,
        `/hooks/stop-call-recording/${INSTANCE_ID_CODE}`,
        stopRecPayload
      );

      setStopRecordingResponse((response.data as { message?: string }).message);
      setEnableStopRecording(false);
    } catch (err) {
      console.error('Error trying to stop call recording:', err);
      setError('Error trying to stop call recording');
    } finally {
      setStopRecordingLoader(false);
    }
  };

  const cleanupCCP = useCallback(() => {
    setCCPInitialized(false);
    setLoggedInCCP(false);
    setInitializationInProgress(false);
  }, []);

  // Initialize CCP
  const initializeCCP = useCallback(
    // eslint-disable-next-line @typescript-eslint/require-await
    async (element: HTMLElement) => {
      if (!authState.loggedIn || !isLeader || ccpInitialized || initializationInProgress) {
        return;
      }

      setInitializationInProgress(true);

      try {
        connect.core.initCCP(element, {
          ccpUrl: `${CCPURL}/ccp-v2`,
          region: REGION,
          loginPopup: true,
          loginPopupAutoClose: true,
          loginOptions: {
            autoClose: true,
            height: 600,
            width: 400,
            top: 0,
            left: 0,
          },
          softphone: {
            allowFramedSoftphone: true,
            disableRingtone: true,
            // Omitted rather than passed as `undefined`: `SoftPhoneOptions.ringtoneUrl`
            // is `string`, and `VITE_CONNECT_RINGTONE_URL` is not in `requiredBuildEnv`
            // (see the note at its declaration). Connect reads this key off the config
            // object, so an absent key and an `undefined` value are the same to it —
            // and `disableRingtone: true` above means it is not played either way.
            ...(RINGTONE_URL === undefined ? {} : { ringtoneUrl: RINGTONE_URL }),
          },
          pageOptions: {
            enableAudioDeviceSettings: true,
            enablePhoneTypeSettings: true,
          },
        });

        setCCPInitialized(true);

        connect.core.onInitialized(() => {
          setLoggedInCCP(true);
          setInitializationInProgress(false);
          sendMessage({ type: 'ccp-initialized' });
        });
      } catch (error) {
        console.error('CCP Initialization Error:', error);
        setInitializationInProgress(false);
        cleanupCCP();
      }
    },
    [authState.loggedIn, isLeader, ccpInitialized, initializationInProgress, cleanupCCP, sendMessage]
  );

  useEffect(() => {
    if (!authState.loggedIn) return;

    if (isLeader) {
      let mutationObserver: MutationObserver | null = null;

      const findAndObserveElement = () => {
        const element = document.getElementById('ccp');

        if (element && element !== ccpElementRef) {
          setCcpElementRef(element);

          // Initialize immediately
          if (!ccpInitialized && !initializationInProgress) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget CCP bootstrap; a rejection leaves the softphone uninitialised, which is what it does today
            initializeCCP(element);
          }
        } else if (!element && ccpElementRef) {
          setCcpElementRef(null);
          if (ccpInitialized) {
            cleanupCCP();
          }
        }
      };

      mutationObserver = new MutationObserver((mutations) => {
        let shouldCheck = false;

        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node as Element;
                if (element.id === 'ccp' || element.querySelector('#ccp')) {
                  shouldCheck = true;
                }
              }
            });

            mutation.removedNodes.forEach((node) => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node as Element;
                if (element.id === 'ccp' || element.querySelector('#ccp')) {
                  shouldCheck = true;
                }
              }
            });
          }
        });

        if (shouldCheck) {
          setTimeout(findAndObserveElement, 50);
        }
      });

      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });

      // Initial check
      findAndObserveElement();

      return () => {
        if (mutationObserver) {
          mutationObserver.disconnect();
        }
      };
    } else {
      const ccpInitializedMessage = messages.find((msg) => msg.type === 'ccp-initialized');
      if (ccpInitializedMessage && !ccpInitialized) {
        setCCPInitialized(true);
        setLoggedInCCP(true);
      }
    }
  }, [authState.loggedIn, isLeader, ccpElementRef, ccpInitialized, messages]);

  const forceReinitializeCCP = useCallback(() => {
    const element = document.getElementById('ccp');

    if (element && authState.loggedIn && isLeader) {
      cleanupCCP();
      setTimeout(() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget CCP bootstrap; a rejection leaves the softphone uninitialised, which is what it does today
        initializeCCP(element);
      }, 300);
    }
  }, [authState.loggedIn, isLeader, cleanupCCP, initializeCCP]);

  useEffect(() => {
    if (!ccpInitialized) return;

    // Asserted to the FLATTENED shape once, here, rather than at each read
    // below: `mobilePhone`, `homePhone`, `fullName` and `orgId` are all
    // `FlatPatient` fields, while every writer of `patient` stores a raw FHIR
    // resource. See the SUSPECTED DEFECT note at the `patient` state.
    const MEMBER = (patient ?? undefined) as FlatPatient | undefined;
    const CURRENT_PATIENT_ID = MEMBER?.id;
    const PRACTITIONER_NAME = appState.practitionerName;

    let orgId: string | undefined;
    setMobilePhoneNumber(MEMBER?.mobilePhone?.[0] as string | undefined);
    setHomePhoneNumber(MEMBER?.homePhone?.[0] as string | undefined);

    const handleContactEvent = (contact: connect.Contact) => {
      // Both from `getPatients`, which resolves to `(Patient | null)[]`.
      let callers: (Patient | null)[] | undefined;
      let DEFAULT_CALLER: Patient | null | undefined;

      const handleOnConnecting = async (contact: connect.Contact) => {
        const contactId = contact.getContactId();
        const origContactId = contact.getOriginalContactId();
        setContactId(contactId);
        setInitialContactId(origContactId);

        if (contact.isInbound()) {
          const attributes = contact.getAttributes();
          let phoneNumber = attributes?.['phoneNumber']?.value;
          phoneNumber = formatInboundPhoneNumber(phoneNumber);
          orgId = attributes?.['orgId']?.value;
          setCallerOrgId(orgId);

          // Same shape as the `phoneNumber` note above: `AttributeDictionary`
          // entries are `{ name, value } | undefined`, so a flow that does not
          // set `orgId` yields `undefined` and the `/Patient/?organization=`
          // query is built with the literal string "undefined". Reported at the
          // `formatInboundPhoneNumber` note; a default here would query the
          // wrong organization instead.
          callers = await getPatients(phoneNumber, orgId!);
          setInboundCallerResources(callers ?? null);
          setPatient(callers ? callers[0] : null);

          DEFAULT_CALLER = callers ? callers[0] : null;
          setIncomingCall(true);
        }
      };

      const handleOnAccepted = () => {
        setCallAnswered(true);
      };

      const handleOnConnected = () => {
        setEnableStopRecording(true);
      };

      const handleOnEnded = () => {
        setEnableStopRecording(false);

        const memberName = MEMBER?.fullName ?? FHIRUtilsCB.parseFHIRPatientName(DEFAULT_CALLER);
        const memberId = CURRENT_PATIENT_ID ?? DEFAULT_CALLER?.id;
        const contactAttributes = contact.getAttributes();
        const contactState = contact.getState();
        const contactId = contact.getContactId();
        const callIsInbound = contact.isInbound();

        const attributes = contact.getAttributes();
        let phoneNumber = attributes?.['phoneNumber']?.value;
        const dialedNumber = attributes?.['dialedNumber']?.value;
        orgId = attributes?.['orgId']?.value;

        phoneNumber = formatInboundPhoneNumber(phoneNumber);

        const commPayload = buildPayload(
          contact,
          selectedMessage!,
          memberName,
          // `AppState['practitionerName']` is `string | null | undefined` — it is
          // seeded `null` and cleared to `null` on logout — while `buildPayload`
          // requires a `string` and uses it as the outbound `sender`.
          PRACTITIONER_NAME!,
          memberId!,
          contactState.type,
          contactId,
          orgId,
          phoneNumber,
          dialedNumber!
        );

        const currentWindow = localStorage.getItem('windowName');
        if (!callIsInbound && currentWindow === MEMBER?.id) {
          if (callSubject !== selectedMessage) {
            // SUSPECTED DEFECT — `orgId` exists on NEITHER shape `patient` can
            // hold: FHIR `Patient` has `managingOrganization`, and `FlatPatient`
            // names the field `org`. So this read is always `undefined` and the
            // outbound communication is logged with no `orgId` whenever
            // `callSubject !== selectedMessage`. Reported, not fixed: writing
            // `org` instead would start attributing these records to an
            // organization they are not attributed to today.
            commPayload.orgId = (patient as unknown as { orgId: string } | undefined)?.orgId as string;
            // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget write; a rejection is dropped and the user sees no failure
            createCommunication(commPayload);
          }
          setCallAnswered(false);
          resetCallCenter();
        } else if (callIsInbound && !MEMBER) {
          setChooseMemberModal(true);
          setCommPayloadState(commPayload);

          if (!DEFAULT_CALLER) {
            commPayload.sender = 'Unknown Caller';
            commPayload.orgId = orgId!;
            // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget write; a rejection is dropped and the user sees no failure
            createCommunication(commPayload);
          }
          // `MEMBER!` — the preceding `else if` took the `!MEMBER` case, which
          // narrowing does not carry across branches.
        } else if (callIsInbound && MEMBER?.id === patient?.id && currentWindow === MEMBER!.id) {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget write; a rejection is dropped and the user sees no failure
          createCommunication(commPayload);
          setCallAnswered(false);
          resetCallCenter();
        } else {
          resetCallCenter();
          setCallAnswered(false);
        }

        setCallerOrgId('');
        //setInboundCallerResources(null);
        //
        //part of the ccp setup, ideally do not touch this if statement unless dire.
        if (contactAttributes['DefaultFlowForAgentUI']) {
          // const contactflowId = contactAttributes['DefaultFlowForAgentUI'].value;
          // const customViewsIframe = await document.querySelector('#customviews-container > iframe');
          // customViewsIframe.setAttribute(
          //   'src',
          //   `${CCPURL}/stargate/app?contactFlowId=${contactflowId}&currentContactId=${currentContactId}`
          // );
        }
      };

      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      contact.onConnecting(handleOnConnecting);
      contact.onAccepted(handleOnAccepted);
      contact.onConnected(handleOnConnected);
      contact.onEnded(handleOnEnded);
    };

    connect.contact(handleContactEvent);

    //resetCallCenter();
    // setCallAnswered(false);
  }, [
    ccpInitialized,
    selectedMessage,
    appState.practitionerName,
    buildPayload,
    callSubject,
    createCommunication,
    getPatients,
    patient,
  ]);

  return (
    <AmazonConnectContext.Provider
      value={{
        ccpInitialized,
        loggedInCCP,
        mobilePhoneNumber,
        callAnswered,
        homePhoneNumber,
        loading,
        patient,
        setPatient,
        chooseMemberModal,
        selectedCallerCard,
        setSelectedCallerCard,
        commPayloadState,
        selectedMessage,
        callerOrgId,
        outboundOrgId,
        setOutboundOrgId,
        inboundCallerResources,
        incomingCall,
        subjectErrorMessage,
        contactId,
        initialContactId,
        enableStopRecording,
        stopRecordingLoader,
        stopRecordingResponse,
        error,
        resetCallCenter,
        buildPayload,
        getPatients,
        formatInboundPhoneNumber,
        handleMessageSelection,
        createCommunication,
        handleInboundCall,
        handleStopRecording,
        showNewPatient,
        isMessagesTab,
        setIsMessagesTab,
        forceReinitializeCCP,
      }}
    >
      {children}
    </AmazonConnectContext.Provider>
  );
};

export const useAmazonConnect = (): AmazonConnectContextType => {
  const context = useContext(AmazonConnectContext);
  if (!context) {
    throw new Error('useAmazonConnect must be used within an AmazonConnectProvider');
  }
  return context;
};
